import { Box, Checkbox, Flex, VStack } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { useRef, useState } from 'react';

import { useTrialUserBlurLogic } from '../../../hooks/useTrialUserBlurLogic';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { TableRow } from '../../TableSelectionProvider/TableSelectionProvider';
import { ContactInfoSection } from '../TalentDiscoveryCardView/ContactInfoSection';
import { EducationHistorySection } from '../TalentDiscoveryCardView/EducationHistorySection';
import { ProfileHeader } from '../TalentDiscoveryCardView/ProfileHeader';
import { ProfileName } from '../TalentDiscoveryCardView/ProfileName';
import { SkillsSection } from '../TalentDiscoveryCardView/SkillsSection';
import { WorkHistorySection } from '../TalentDiscoveryCardView/WorkHistorySection';
import { CardPopout, PopoutContentSelector } from './RowPopoutSelector';
import styles from './TableChart.module.css';
import cardStyles from './TalentDiscoveryCardView.module.css';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const parseUserKey = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

interface TalentDiscoveryCardViewProps {
  data: Array<Record<string, unknown>>;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (rows: TableRow[]) => void;
  selectedRows?: TableRow[];
  showZebraStripe?: boolean;
  popoutChartType: ChartType;
}

export const TalentDiscoveryCardView = ({
  data,
  enableRowSelection,
  onRowSelectionChange,
  selectedRows = [],
  showZebraStripe = true,
  popoutChartType,
}: TalentDiscoveryCardViewProps) => {
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();

  const [expandedSkillsCards, setExpandedSkillsCards] = useState<Set<number>>(
    new Set()
  );

  const [expandedWorkHistoryCards, setExpandedWorkHistoryCards] = useState<
    Set<number>
  >(new Set());

  const [expandedEducationHistoryCards, setExpandedEducationHistoryCards] =
    useState<Set<number>>(new Set());

  const selectedUserIds = new Set(
    selectedRows.map((row) => (row as Record<string, unknown>).user_id)
  );

  const handleCardClick = (cardData: Record<string, unknown>) => {
    if (!enableRowSelection || !onRowSelectionChange) return;

    const userId = cardData.user_id;
    const isSelected = selectedUserIds.has(userId);

    let newSelection: TableRow[];
    if (isSelected) {
      newSelection = selectedRows.filter(
        (row) => (row as Record<string, unknown>).user_id !== userId
      );
    } else {
      newSelection = [...selectedRows, cardData];
    }

    onRowSelectionChange(newSelection);
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const popoutGridRef = useRef({
    getScrollLeft: () => parentRef.current?.scrollLeft ?? 0,
    getDOMNode: () => parentRef.current,
  }).current;

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 280,
    overscan: 5,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 280,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <Box ref={parentRef} w="full" h="full" overflow="auto" position="relative">
      <Box
        position="relative"
        width="100%"
        height={`${virtualizer.getTotalSize()}px`}
      >
        {virtualItems.map((virtualRow) => {
          const index = virtualRow.index;
          const cardData = data[index];
          if (!cardData) return null;
          const isLastCard = index === data.length - 1;
          const historyData = (
            Array.isArray(cardData.work_history) ? cardData.work_history : []
          ) as Array<Record<string, unknown>>;

          const sortedHistory = historyData.sort((a, b) => {
            const aDate = String(a.start_date ?? '');
            const bDate = String(b.start_date ?? '');
            if (aDate > bDate) return -1;
            if (aDate < bDate) return 1;
            return 0;
          });

          const currentPosition = sortedHistory[0];
          const userId = cardData.user_id;
          const isSelected = selectedUserIds.has(userId);

          const firstName = String(cardData.first_name ?? '');
          const lastName = String(cardData.last_name ?? '');
          const currentTitle = String(
            cardData.current_title ?? currentPosition?.current_title ?? ''
          );
          const location = String(
            cardData.user_location ?? cardData.location ?? ''
          );
          const yearsOfExperience = (() => {
            const value = cardData.years_of_experience;
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            if (typeof value === 'number' && !isNaN(value)) return value;
            if (typeof value === 'string') {
              const parsed = Number(value);
              // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
              return !isNaN(parsed) ? parsed : undefined;
            }
            return undefined;
          })();

          const skills = isStringArray(cardData.skills) ? cardData.skills : [];
          const isSkillsExpanded = expandedSkillsCards.has(index);
          const isWorkHistoryExpanded = expandedWorkHistoryCards.has(index);

          const educationHistoryData = (
            Array.isArray(cardData.education_history)
              ? cardData.education_history
              : []
          ) as Array<Record<string, unknown>>;

          const isEducationHistoryExpanded =
            expandedEducationHistoryCards.has(index);

          const email = String(cardData.email ?? '');
          const personalEmail = String(cardData.personal_email ?? '');
          const phoneNumber = String(cardData.phone_number ?? '');
          const profileUrl = String(cardData.linkedin_url ?? '');
          const rowRecord = cardData as TableRow;
          const originalValues = rowRecord.__originalValues ?? {};
          const userKey =
            parseUserKey(cardData.original_user_id) ??
            parseUserKey(cardData.user_id);

          // Row background: selected wins over zebra, both over the base card.
          // Colors live in TalentDiscoveryCardView.module.css so they can flip
          // by theme; legacy values are preserved there.
          let cardBgClass: string | undefined;
          if (isSelected) {
            cardBgClass = cardStyles.cardSelected;
          } else if (showZebraStripe && index % 2 === 0) {
            cardBgClass = cardStyles.cardZebra;
          }

          const cardContent = (
            <Flex gap={3} align="start">
              {enableRowSelection && (
                <Checkbox
                  isChecked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleCardClick(cardData)}
                  colorScheme="green"
                  mt="3px"
                />
              )}

              <VStack align="stretch" flex={1} spacing={3}>
                <VStack align="stretch" spacing={1}>
                  <ProfileName
                    firstName={firstName}
                    lastName={lastName}
                    isTrialUser={isTrialUser}
                    encryptedLastName={originalValues['last_name']}
                    userKey={userKey}
                    onBlurHover={showBlurToast}
                  />

                  <ProfileHeader
                    currentTitle={currentTitle}
                    yearsOfExperience={yearsOfExperience}
                    location={location}
                  />
                </VStack>

                <WorkHistorySection
                  workHistory={historyData}
                  isExpanded={isWorkHistoryExpanded}
                  onToggleExpanded={() => {
                    setExpandedWorkHistoryCards((prev) => {
                      const next = new Set(prev);
                      if (next.has(index)) {
                        next.delete(index);
                      } else {
                        next.add(index);
                      }
                      return next;
                    });
                  }}
                />

                <EducationHistorySection
                  educationHistory={educationHistoryData}
                  isExpanded={isEducationHistoryExpanded}
                  onToggleExpanded={() => {
                    setExpandedEducationHistoryCards((prev) => {
                      const next = new Set(prev);
                      if (next.has(index)) {
                        next.delete(index);
                      } else {
                        next.add(index);
                      }
                      return next;
                    });
                  }}
                />

                <SkillsSection
                  skills={skills}
                  isExpanded={isSkillsExpanded}
                  onToggleExpanded={() => {
                    setExpandedSkillsCards((prev) => {
                      const next = new Set(prev);
                      if (next.has(index)) {
                        next.delete(index);
                      } else {
                        next.add(index);
                      }
                      return next;
                    });
                  }}
                />

                <ContactInfoSection
                  email={email}
                  personalEmail={personalEmail}
                  phoneNumber={phoneNumber}
                  profileUrl={profileUrl}
                  isTrialUser={isTrialUser}
                  encryptedEmail={originalValues['email']}
                  encryptedPersonalEmail={originalValues['personal_email']}
                  encryptedPhoneNumber={originalValues['phone_number']}
                  encryptedProfileUrl={originalValues['linkedin_url']}
                  userKey={userKey}
                  onBlurHover={showBlurToast}
                />
              </VStack>
            </Flex>
          );

          const commonBoxProps = {
            dataIndex: index,
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
            className: classNames(
              cardStyles.card,
              cardBgClass,
              styles.cardItem,
              isLastCard && styles.cardItemLast
            ),
          };

          return (
            <CardPopout
              key={String(userId)}
              boundaryRef={parentRef}
              gridRef={popoutGridRef}
              rowData={cardData as TableRow}
              content={
                <PopoutContentSelector
                  chartType={popoutChartType}
                  rowData={cardData as TableRow}
                />
              }
            >
              {({ onClick, anchorRef }) => (
                <Box
                  data-index={commonBoxProps.dataIndex}
                  ref={(node: HTMLDivElement | null) => {
                    virtualizer.measureElement(node);
                    anchorRef(node);
                  }}
                  position={commonBoxProps.position}
                  top={commonBoxProps.top}
                  left={commonBoxProps.left}
                  width={commonBoxProps.width}
                  transform={commonBoxProps.transform}
                  className={commonBoxProps.className}
                  onClick={onClick}
                >
                  {cardContent}
                </Box>
              )}
            </CardPopout>
          );
        })}
      </Box>
    </Box>
  );
};
